import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Flame, Globe, Palmtree } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { CopyableId } from '@/components/app/CopyableId';
import { DataTable } from '@/components/app/DataTable';
import { ErrorState } from '@/components/app/ErrorState';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { humanizeError } from '@/lib/apiClient';
import { shortId } from '@/lib/utils';
import { getAvatarUrl } from '@/lib/cdn';
import { useUsersList } from '../hooks';
import type { UserListItem } from '../types';

type ActiveFilter = 'all' | 'active' | 'inactive';

function debounce<T extends (...args: never[]) => void>(fn: T, delay: number) {
  let t: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

function formatRelative(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

export function UsersListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState<ActiveFilter>('all');
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);

  const debouncedSet = React.useMemo(
    () =>
      debounce((value: string) => {
        setDebounced(value);
        setCursor(undefined);
        setCursorStack([]);
      }, 350),
    [],
  );

  const isActive = activeFilter === 'all' ? undefined : activeFilter === 'active';

  const params = {
    limit: 50,
    cursor,
    search: debounced || undefined,
    isActive,
  };

  const usersQuery = useUsersList(params);

  const columns = React.useMemo<ColumnDef<UserListItem>[]>(() => {
    return [
      {
        id: 'identity',
        header: 'User',
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="flex items-center gap-3 min-w-0">
              <Avatar className="h-12 w-12">
                <AvatarImage src={getAvatarUrl(u.avatarUrl)} alt="" />
                <AvatarFallback>
                  {(u.displayName ?? u.email).slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {u.displayName ?? u.email.split('@')[0]}
                </div>
                <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                <CopyableId
                  value={u.userId}
                  display={shortId(u.userId, 8, 6)}
                  label="User ID"
                  className="-ml-1.5 mt-0.5"
                />
              </div>
            </div>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {u.isActive ? (
                <Badge variant="success">Active</Badge>
              ) : (
                <Badge variant="outline">Inactive</Badge>
              )}
              {u.isOnHoliday && (
                <Badge variant="warning">
                  <Palmtree className="mr-1 h-3 w-3" /> Holiday
                </Badge>
              )}
              {u.onboardingPassed === false && <Badge variant="outline">Onboarding</Badge>}
            </div>
          );
        },
      },
      {
        id: 'streak',
        header: 'Streak',
        cell: ({ row }) =>
          row.original.userStreak ? (
            <span className="inline-flex items-center gap-1 text-sm">
              <Flame className="h-3.5 w-3.5 text-amber-500" />
              {row.original.userStreak}d
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'timezone',
        header: 'Timezone',
        cell: ({ row }) =>
          row.original.timeZone ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Globe className="h-3 w-3" />
              {row.original.timeZone}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'lastLogin',
        header: 'Last seen',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground" title={row.original.lastLoginAt}>
            {formatRelative(row.original.lastLoginAt)}
          </span>
        ),
      },
      {
        id: 'createdAt',
        header: 'Joined',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.createdAt?.slice(0, 10) ?? '—'}
          </span>
        ),
      },
    ];
  }, []);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        description="Search, filter, and drill into any user profile. Destructive actions live on the detail view."
        actions={
          <Select
            value={activeFilter}
            onValueChange={(v) => {
              setActiveFilter(v as ActiveFilter);
              setCursor(undefined);
              setCursorStack([]);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {usersQuery.isError ? (
        <ErrorState
          title="Users failed to load"
          description={humanizeError(usersQuery.error)}
          actionLabel="Retry"
          onAction={() => usersQuery.refetch()}
        />
      ) : (
        <DataTable<UserListItem>
          columns={columns}
          data={usersQuery.data?.users ?? []}
          loading={usersQuery.isLoading || usersQuery.isFetching}
          emptyLabel={debounced ? 'No users match your search' : 'No users found'}
          emptyDescription={debounced ? 'Try a different name or email.' : undefined}
          rowKey={(r) => r.userId}
          onRowClick={(r) => navigate(`/users/${encodeURIComponent(r.userId)}`)}
          globalFilter={search}
          onGlobalFilterChange={(v) => {
            setSearch(v);
            debouncedSet(v);
          }}
          searchPlaceholder="Search by name or email…"
          pagination={{
            cursor: cursor ?? null,
            nextCursor: usersQuery.data?.nextCursor ?? null,
            prevCursor: cursorStack.length > 0 ? (cursorStack[cursorStack.length - 1] ?? null) : null,
            hasMore: !!usersQuery.data?.nextCursor,
            onNext: usersQuery.data?.nextCursor
              ? () => {
                  setCursorStack((stack) => [...stack, cursor ?? '']);
                  setCursor(usersQuery.data?.nextCursor ?? undefined);
                }
              : undefined,
            onPrev:
              cursorStack.length > 0
                ? () => {
                    const prev = cursorStack[cursorStack.length - 1] || undefined;
                    setCursor(prev);
                    setCursorStack((stack) => stack.slice(0, -1));
                  }
                : undefined,
          }}
        />
      )}
    </div>
  );
}
